// Silhouette snapping — pulls "outer" mesh vertices to the nearest point on
// the person-mask boundary. Makes the mesh conform to the rider's actual
// outline instead of the straight-line approximation from landmarks alone.
//
// Approach:
//   1. We assume the caller has an edgeMap (produced by contour.ts) — a
//      binary bitmap where 1 = boundary pixel.
//   2. For each candidate vertex (expressed in mask pixel coords), we walk
//      outward from the vertex along the direction *away from the body
//      centroid*, and find the nearest edge pixel. We snap the vertex to
//      that pixel.
//   3. If no edge is found within a max search radius, we leave the vertex
//      unchanged.
//
// This only makes sense for body-perimeter vertices (shoulders, hips,
// elbows, wrists, knees, ankles — not midline or face). The caller passes
// in which vertices to snap via a boolean mask.

export interface SnapResult {
  /** For each input vertex, either the snapped pixel coords or the original. */
  x: number;
  y: number;
  snapped: boolean;
}

export function snapToSilhouette(
  /** Source vertex positions in mask pixel coords. */
  vertices: Array<{ x: number; y: number }>,
  /** Which vertex indices should be snapped. */
  shouldSnap: boolean[],
  /** Flat W*H bitmap with 1 at boundary pixels. */
  edgeMap: Uint8Array,
  maskW: number,
  maskH: number,
  /** Body centroid in mask pixel coords (to determine outward direction). */
  centroidX: number,
  centroidY: number,
  /** Max distance in pixels to search for an edge. */
  maxRadius: number = 40
): SnapResult[] {
  const out: SnapResult[] = new Array(vertices.length);

  for (let i = 0; i < vertices.length; i++) {
    const v = vertices[i];
    if (!shouldSnap[i]) {
      out[i] = { x: v.x, y: v.y, snapped: false };
      continue;
    }

    // Direction from centroid to vertex, normalized
    let dx = v.x - centroidX;
    let dy = v.y - centroidY;
    const mag = Math.hypot(dx, dy);
    if (mag < 1e-3) {
      out[i] = { x: v.x, y: v.y, snapped: false };
      continue;
    }
    dx /= mag;
    dy /= mag;

    // Walk a pixel at a time in both directions along the outward ray,
    // find the nearest edge. We walk outward first (up to maxRadius), then
    // inward (also up to maxRadius) to handle vertices that sit outside
    // the silhouette.
    let bestDist = Infinity;
    let bestX = v.x;
    let bestY = v.y;

    for (let r = 0; r <= maxRadius; r++) {
      // Outward
      const px = Math.round(v.x + dx * r);
      const py = Math.round(v.y + dy * r);
      if (px >= 0 && px < maskW && py >= 0 && py < maskH) {
        if (edgeMap[py * maskW + px]) {
          if (r < bestDist) {
            bestDist = r;
            bestX = px;
            bestY = py;
          }
          break;
        }
      }
      // Inward
      if (r > 0) {
        const qx = Math.round(v.x - dx * r);
        const qy = Math.round(v.y - dy * r);
        if (qx >= 0 && qx < maskW && qy >= 0 && qy < maskH) {
          if (edgeMap[qy * maskW + qx]) {
            if (r < bestDist) {
              bestDist = r;
              bestX = qx;
              bestY = qy;
            }
            break;
          }
        }
      }
    }

    if (bestDist < Infinity) {
      out[i] = { x: bestX, y: bestY, snapped: true };
    } else {
      out[i] = { x: v.x, y: v.y, snapped: false };
    }
  }

  return out;
}
